import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { SOLUTIONS, PAGE } from "@/lib/pageContent";

export default function Solutions() {
  const { lang, t } = useI18n();
  const solutions = pickLang(SOLUTIONS, lang);
  const p = pickLang(PAGE, lang);

  return (
    <div data-testid="solutions-page">
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="overline">{t("nav.solutions")}</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] max-w-4xl text-white">
            {p.solutions_title_a} <span className="gradient-text">{p.solutions_title_b}</span>
          </h1>
          <p className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed">{p.solutions_subtitle}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {solutions.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-hover glass rounded-3xl p-7 md:p-10 flex flex-col"
                data-testid={`solution-card-${s.slug}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{`0${i + 1} / 0${solutions.length}`}</div>
                    <h3 className="font-display mt-3 text-2xl md:text-3xl font-bold tracking-tight text-white">{s.title}</h3>
                  </div>
                  <ArrowUpRight size={22} className="text-slate-400 shrink-0" />
                </div>
                <p className="mt-5 text-slate-200 leading-relaxed">{s.description}</p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {s.points.map((pt, j) => (
                    <div key={j} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-slate-200">
                      {pt}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 border-t border-white/10 bg-[#324567]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {p.solutions_cta_title_a} <span className="gradient-text">{p.solutions_cta_title_b}</span>
          </h2>
          <p className="mt-6 text-slate-200">{p.solutions_cta_sub}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="solutions-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="solutions-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
